import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import styles from '../css-isolated/consult_seach.module.css';
import authFetch from '../utils/authFetch';
import { lettersOnly } from '../utils/inputValidation';

function ConsultSearch() {
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [userData, setUserData] = useState(null);
  const [searchData, setSearchData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    aka: ''
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPDLForConfirm, setSelectedPDLForConfirm] = useState(null);
  const [pdlDetailsLoading, setPdlDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedPDL, setSelectedPDL] = useState(null);
  
  // Schedule checkup modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedPDLForSchedule, setSelectedPDLForSchedule] = useState(null);
  const [showScheduleSuccessModal, setShowScheduleSuccessModal] = useState(false);
  const [scheduleSuccessData, setScheduleSuccessData] = useState(null);
  
  // Follow-up date picker modal state for scheduling
  const [showFollowUpDateModal, setShowFollowUpDateModal] = useState(false);
  const [followUpCurrentDate, setFollowUpCurrentDate] = useState(new Date());
  const [followUpSelectedDates, setFollowUpSelectedDates] = useState([]);
  const [followUpRecords, setFollowUpRecords] = useState([]); // Store records with IDs
  
  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme !== 'light';
  });
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Load user data on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load selected PDL from localStorage on mount
  useEffect(() => {
    const storedPDL = localStorage.getItem('selectedPDL');
    if (storedPDL) {
      try {
        const pdlData = JSON.parse(storedPDL);
        setSelectedPDL(pdlData);
      } catch (error) {
        console.error('Error parsing selected PDL:', error);
      }
    }
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    navigate('/login');
  };
  
  const toggleDarkMode = () => {
    setPendingTheme(!isDarkMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.user_id) {
        await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user.user_id,
            is_darkmode: newTheme
          }),
        });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
    
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${styles['profile-section']}`)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSearch = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    
    try {
      // Prepare search parameters to match backend API expectations
      const searchParams = new URLSearchParams();
      if (searchData.lastName) searchParams.append('lastName', searchData.lastName);
      if (searchData.firstName) searchParams.append('firstName', searchData.firstName);
      if (searchData.middleName) searchParams.append('middleName', searchData.middleName);
      if (searchData.aka) searchParams.append('akaNickname', searchData.aka);

      // API call to search hr_info table - if no params, it will return all records
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?${searchParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const results = await response.json();
      
      // Transform the results to match the expected format
      // Exclude PDLs that have a final remark (released, transferred, deceased, etc.)
      const transformedResults = results
        .filter(item => !item.final_remarks)
        .map(item => ({
        id: item.id,
        last_name: item.last_name || item.surname,
        first_name: item.first_name || item.firstName,
        middle_name: item.middle_name || item.middleName,
        aka: item.alias_nickname || item.aka || 'N/A',
        date_of_birth: item.date_of_birth || item.dateOfBirth,
        current_cell: item.current_cell || item.currentCell
      }));
      
      setSearchResults(transformedResults);
      setCurrentPage(1);
      setIsLoading(false);
    } catch (error) {
      console.error('Error searching HR info records:', error);
      setSearchResults([]);
      setCurrentPage(1);
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchData({
      lastName: '',
      firstName: '',
      middleName: '',
      aka: ''
    });
    setSearchResults([]);
    setCurrentPage(1);
  };

  // Pagination calculations
  const totalPages = Math.ceil(searchResults.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchResults.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectRecord = async (record) => {
    setPdlDetailsLoading(true);
    
    try {
      // Fetch complete PDL record from hr_info table with cell relationship
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const fullRecord = await response.json();
        // Set the selected PDL for confirmation modal
        setSelectedPDLForConfirm(fullRecord);
        setShowConfirmModal(true);
      } else {
        console.error('Failed to fetch full PDL record');
        // Still show confirmation with basic info we have
        setSelectedPDLForConfirm(record);
        setShowConfirmModal(true);
      }
    } catch (error) {
      console.error('Error fetching full PDL record:', error);
      // Show confirmation with basic record info we have
      setSelectedPDLForConfirm(record);
      setShowConfirmModal(true);
    } finally {
      setPdlDetailsLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    try {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      
      if (isNaN(birthDate.getTime())) {
        return 'N/A';
      }
      
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age.toString();
    } catch (error) {
      console.error('Error calculating age:', error);
      return 'N/A';
    }
  };

  const confirmSelection = () => {
    if (!selectedPDLForConfirm) return;
    // Format PDL data with proper structure including photo
    const pdlDataForStorage = {
      id: selectedPDLForConfirm.id,
      fullName: `${selectedPDLForConfirm.first_name || ''} ${selectedPDLForConfirm.middle_name || ''} ${selectedPDLForConfirm.last_name || ''}`.trim(),
      alsoKnownAs: selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || '',
      age: selectedPDLForConfirm.age || calculateAge(selectedPDLForConfirm.date_of_birth),
      currentCell: selectedPDLForConfirm.cell_no || selectedPDLForConfirm.cell?.cell_no || 'Unassigned',
      photo: selectedPDLForConfirm.photo_of_pdl || null
    };
    
    // Store the formatted PDL data for use in consultation
    localStorage.setItem('selectedPDL', JSON.stringify(pdlDataForStorage));
    // Update the displayed selected PDL
    setSelectedPDL(pdlDataForStorage);
    
    // Close modal and show success
    setShowConfirmModal(false);
    alert(`PDL Selected Successfully!\n\n${selectedPDLForConfirm.first_name} ${selectedPDLForConfirm.last_name} has been selected for consultation.`);
    
    // Optional: Navigate to consultation form
    // navigate('/health-consultation', { state: { selectedPDL: selectedPDLForConfirm } });
  };

  const cancelSelection = () => {
    setShowConfirmModal(false);
    setSelectedPDLForConfirm(null);
  };

  const clearSelectedPDL = () => {
    setSelectedPDL(null);
    localStorage.removeItem('selectedPDL');
  };

  const handleScheduleCheckup = async (record) => {
    setPdlDetailsLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`);
      if (response.ok) {
        const fullData = await response.json();
        setSelectedPDLForSchedule(fullData);
        
        // Fetch existing follow-up consultations and pre-populate selected dates
        try {
          const followUpResponse = await authFetch(`${API_BASE_URL}/api/followup-consult/patient/${record.id}`);
          if (followUpResponse.ok) {
            const followUpData = await followUpResponse.json();
            // Store full records with IDs
            setFollowUpRecords(followUpData);
            // Extract dates for display
            const dates = followUpData.map(item => new Date(item.date)).filter(date => !isNaN(date));
            setFollowUpSelectedDates(dates);
          } else {
            setFollowUpRecords([]);
            setFollowUpSelectedDates([]);
          }
        } catch (followUpError) {
          console.error('Error fetching follow-up consultations:', followUpError);
          setFollowUpRecords([]);
          setFollowUpSelectedDates([]);
        }
        
        setShowScheduleModal(true);
      }
    } catch (error) {
      console.error('Error fetching PDL details:', error);
      alert('Failed to load PDL details');
    } finally {
      setPdlDetailsLoading(false);
    }
  };

  const confirmScheduleCheckup = () => {
    // Close the PDL confirmation modal and open date picker
    setShowScheduleModal(false);
    setShowFollowUpDateModal(true);
  };

  const finalizeScheduleCheckup = async () => {
    if (followUpSelectedDates.length === 0) {
      alert('Please select at least one date for the checkup');
      return;
    }

    try {
      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = currentUser.id;

      // Filter out dates that already exist in the database
      const existingDates = followUpRecords.map(record => new Date(record.date).toDateString());
      const newDates = followUpSelectedDates.filter(date => !existingDates.includes(date.toDateString()));

      // Only submit if there are new dates to add
      if (newDates.length > 0) {
        // Prepare follow-up consultation payload with only new dates
        const followUpPayload = {
          hr_info_id: selectedPDLForSchedule.id,
          dates: newDates.map(date => {
            // Format date as YYYY-MM-DD for the backend
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }),
          nod_md: currentUserId ? parseInt(currentUserId) : null
        };
        const response = await authFetch(`${API_BASE_URL}/api/followup-consult`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(followUpPayload),
        });

        if (!response.ok) {
          let errorDetails;
          try {
            errorDetails = await response.json();
          } catch {
            errorDetails = await response.text();
          }
          console.error('Schedule checkup submission error:', errorDetails);
          throw new Error('Failed to save scheduled checkup');
        }

        await response.json();
      } else {
      }

      // Store success data and show success modal
      setScheduleSuccessData({
        pdlName: `${selectedPDLForSchedule.first_name} ${selectedPDLForSchedule.last_name}`,
        dates: followUpSelectedDates
      });
      
      // Close date picker modal and reset state
      setShowFollowUpDateModal(false);
      setSelectedPDLForSchedule(null);
      setFollowUpSelectedDates([]);
      
      // Show success modal
      setShowScheduleSuccessModal(true);
    } catch (error) {
      console.error('Error scheduling checkup:', error);
      alert('Failed to schedule checkup. Please try again.');
    }
  };

  const cancelScheduleCheckup = () => {
    setShowScheduleModal(false);
    setSelectedPDLForSchedule(null);
  };

  const cancelScheduleDatePicker = () => {
    setShowFollowUpDateModal(false);
    setFollowUpSelectedDates([]);
    setSelectedPDLForSchedule(null);
  };

  const toggleDateSelection = async (date) => {
    const dateStr = date.toDateString();
    const exists = followUpSelectedDates.some(d => d.toDateString() === dateStr);
    
    if (exists) {
      // Check if this date has an existing record in the database
      const existingRecord = followUpRecords.find(record => {
        const recordDate = new Date(record.date);
        return recordDate.toDateString() === dateStr;
      });
      
      if (existingRecord) {
        // Delete from database
        try {
          const response = await authFetch(`${API_BASE_URL}/api/followup-consult/${existingRecord.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (response.ok) {
            // Remove from records array
            setFollowUpRecords(prev => prev.filter(r => r.id !== existingRecord.id));
          } else {
            console.error('Failed to delete follow-up consultation');
            alert('Failed to delete the scheduled checkup');
            return; // Don't remove from UI if deletion failed
          }
        } catch (error) {
          console.error('Error deleting follow-up consultation:', error);
          alert('Error deleting the scheduled checkup');
          return; // Don't remove from UI if deletion failed
        }
      }
      
      // Remove from selected dates
      setFollowUpSelectedDates(prev => prev.filter(d => d.toDateString() !== dateStr));
    } else {
      // Add new date
      setFollowUpSelectedDates(prev => [...prev, date]);
    }
  };

  const clearAllSelectedDates = async () => {
    // Delete all existing records from database
    if (followUpRecords.length > 0) {
      try {
        const deletePromises = followUpRecords.map(record =>
          authFetch(`${API_BASE_URL}/api/followup-consult/${record.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          })
        );
        
        await Promise.all(deletePromises);
        setFollowUpRecords([]);
      } catch (error) {
        console.error('Error deleting follow-up consultations:', error);
        alert('Error deleting some scheduled checkups');
      }
    }
    
    setFollowUpSelectedDates([]);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    
    setIsDeleting(true);
    setDeleteError('');
    
    try {
      // Simulate API call - replace with actual delete endpoint
      setTimeout(() => {
        setSearchResults(prevResults => prevResults.filter(r => r.id !== recordToDelete.id));
        setDeleteSuccess(true);
        
        setTimeout(() => {
          setShowDeleteModal(false);
          setRecordToDelete(null);
          setDeleteSuccess(false);
        }, 1500);
        
        setIsDeleting(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error deleting consultation record:', error);
      setDeleteError('Failed to delete the consultation record. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className={`${styles.systemApp} ${isDarkMode ? '' : styles.lightMode}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <button 
            type="button" 
            className={styles.dashboardBtn}
            onClick={() => navigate('/dashboard')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Dashboard
          </button>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={styles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <div className={styles.profileSection}>
            <div className={styles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <button 
                  className={styles.dropdownItem}
                  onClick={toggleDarkMode}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* Page Header */}
          <div className={styles.headerContainer}>
            <div className={styles.headerCard}>
              <h1 className={styles.mainTitle}>PDL Records Search</h1>
              <p className={styles.subTitle}>Search and manage PDL records</p>
            </div>
          </div>

          {/* Selected PDL Display */}
          {selectedPDL && (
            <div className={styles.selectedPdlContainer}>
              <div className={styles.selectedPdlCard}>
                <div className={styles.selectedPdlHeader}>
                  <div className={styles.selectedPdlHeaderTitle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                      <path d="M20 8V14M23 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <h3>Selected PDL</h3>
                  </div>
                  <button 
                    type="button" 
                    onClick={clearSelectedPDL}
                    className={styles.clearPdlButton}
                    title="Clear selection"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className={styles.selectedPdlContent}>
                  <div className={styles.selectedPdlInfo}>
                    <div className={styles.selectedPdlRow}>
                      <span className={styles.selectedPdlLabel}>Full Name:</span>
                      <span className={styles.selectedPdlValue}>{selectedPDL.fullName}</span>
                    </div>
                    <div className={styles.selectedPdlRow}>
                      <span className={styles.selectedPdlLabel}>Also Known As:</span>
                      <span className={styles.selectedPdlValue}>{selectedPDL.alsoKnownAs || 'N/A'}</span>
                    </div>
                    <div className={styles.selectedPdlRow}>
                      <span className={styles.selectedPdlLabel}>Age:</span>
                      <span className={styles.selectedPdlValue}>{selectedPDL.age}</span>
                    </div>
                    <div className={styles.selectedPdlRow}>
                      <span className={styles.selectedPdlLabel}>Current Cell:</span>
                      <span className={styles.selectedPdlValue}>{selectedPDL.currentCell || 'Unassigned'}</span>
                    </div>
                  </div>
                  <div className={styles.selectedPdlPhoto}>
                    {selectedPDL.photo ? (
                      <>
                        <img 
                          src={`/api/storage/${selectedPDL.photo}`} 
                          alt={selectedPDL.fullName}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className={styles.photoPlaceholder} style={{display: 'none'}}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <span>No Photo</span>
                        </div>
                      </>
                    ) : (
                      <div className={styles.photoPlaceholder}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <span>No Photo</span>
                        </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Form Section */}
          <div className={`${styles.formSection} ${styles.searchFormSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                  <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Search Criteria</h2>
            </div>
            
            <form onSubmit={handleSearch} className={styles.searchForm}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Last Name</label>
                  <input 
                    type="text" 
                    name="lastName" 
                    value={searchData.lastName} 
                    onChange={handleInputChange} 
                    onKeyDown={lettersOnly}
                    className={styles.formInput} 
                    placeholder="Enter last name"
                    autoComplete="off" 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>First Name</label>
                  <input 
                    type="text" 
                    name="firstName" 
                    value={searchData.firstName} 
                    onChange={handleInputChange} 
                    onKeyDown={lettersOnly}
                    className={styles.formInput} 
                    placeholder="Enter first name"
                    autoComplete="off" 
                  />
                </div>
              </div>
              
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Middle Name</label>
                  <input 
                    type="text" 
                    name="middleName" 
                    value={searchData.middleName} 
                    onChange={handleInputChange} 
                    onKeyDown={lettersOnly}
                    className={styles.formInput} 
                    placeholder="Enter middle name"
                    autoComplete="off" 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>AKA</label>
                  <input 
                    type="text" 
                    name="aka" 
                    value={searchData.aka} 
                    onChange={handleInputChange} 
                    onKeyDown={lettersOnly}
                    className={styles.formInput} 
                    placeholder="Also Known As"
                    autoComplete="off" 
                  />
                </div>
              </div>
              
              <div className={styles.formActions}>
                <button 
                  type="button" 
                  onClick={handleClearSearch} 
                  className={styles.clearButton}
                  disabled={isLoading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Clear
                </button>
                <button 
                  type="submit" 
                  className={styles.searchButton}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className={styles.spinner}></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                        <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Search PDL
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Search Results Section */}
          {searchResults.length > 0 && (
            <div className={styles.resultsTableCard}>
              <div className={styles.tableCardHeader}>
                <h3>Search Results</h3>
                <span className={styles.recordCount}>Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, searchResults.length)} of {searchResults.length}</span>
              </div>
              
              <div className={styles.tableWrapper}>
                <table className={styles.resultsTable}>
                  <thead>
                    <tr>
                      <th>Current Cell</th>
                      <th>Last Name</th>
                      <th>First Name</th>
                      <th>Middle Name</th>
                      <th>AKA</th>
                      <th>Age</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((record) => (
                      <tr key={record.id}>
                        <td>
                          <span className={styles.cellBadge}>
                            {record.current_cell || 'UNASSIGNED'}
                          </span>
                        </td>
                        <td>{record.last_name}</td>
                        <td>{record.first_name}</td>
                        <td>{record.middle_name}</td>
                        <td>{record.aka || 'N/A'}</td>
                        <td>{calculateAge(record.date_of_birth)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button 
                              onClick={() => handleSelectRecord(record)} 
                              className={styles.selectButton}
                              title="Select Record"
                              disabled={pdlDetailsLoading}
                            >
                              {pdlDetailsLoading ? (
                                <>
                                  <div className={styles.spinner}></div>
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  Select
                                </>
                              )}
                            </button>
                            <button 
                              onClick={() => handleScheduleCheckup(record)} 
                              className={styles.scheduleButton}
                              title="Schedule Checkup"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Schedule
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <button 
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={styles.paginationButton}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Previous
                  </button>
                  
                  <div className={styles.paginationNumbers}>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`${styles.paginationNumber} ${currentPage === pageNumber ? styles.active : ''}`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return <span key={pageNumber} className={styles.paginationEllipsis}>...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button 
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={styles.paginationButton}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* PDL Selection Confirmation Modal */}
      {showConfirmModal && selectedPDLForConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModalContent}>
            <div className={styles.modalHeader}>
              <h3>Confirm PDL Selection</h3>
              <button onClick={cancelSelection} className={styles.closeButton}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.confirmMessage}>
                <p>Are you sure you want to select this PDL for consultation?</p>
              </div>
              
              <div className={styles.pdlDetailsCard}>
                {/* PDL Photo Section */}
                <div className={styles.pdlPhotoSection}>
                  {selectedPDLForConfirm.photo_of_pdl ? (
                    <img 
                      src={`/api/storage/${selectedPDLForConfirm.photo_of_pdl}`} 
                      alt="PDL"
                      className={styles.pdlPhoto}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={styles.pdlPhotoPlaceholder} style={{display: selectedPDLForConfirm.photo_of_pdl ? 'none' : 'flex'}}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>No Photo</span>
                  </div>
                </div>

                {/* PDL Details */}
                <div className={styles.pdlDetailsGrid}>
                  <div className={styles.pdlDetailItem}>
                    <label>Full Name:</label>
                    <span>
                      {selectedPDLForConfirm.last_name}, {selectedPDLForConfirm.first_name} 
                      {selectedPDLForConfirm.middle_name && ` ${selectedPDLForConfirm.middle_name}`}
                    </span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>AKA / Alias:</label>
                    <span>{selectedPDLForConfirm.alias_nickname || selectedPDLForConfirm.aka || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Sex:</label>
                    <span>{selectedPDLForConfirm.sex === 'M' ? 'Male' : selectedPDLForConfirm.sex === 'F' ? 'Female' : selectedPDLForConfirm.sex || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Age:</label>
                    <span>{calculateAge(selectedPDLForConfirm.date_of_birth)} years old</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Civil Status:</label>
                    <span>{selectedPDLForConfirm.civil_status || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Current Cell:</label>
                    <span>
                      {selectedPDLForConfirm.cell?.cell_no || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.confirmActions}>
                <button 
                  onClick={cancelSelection} 
                  className={styles.cancelConfirmButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmSelection} 
                  className={styles.confirmSelectButton}
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Checkup Modal */}
      {showScheduleModal && selectedPDLForSchedule && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModalContent}>
            <div className={styles.modalHeader}>
              <h3>Schedule Checkup</h3>
              <button 
                onClick={cancelScheduleCheckup}
                className={styles.closeButton}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.confirmMessage}>
                <p>Schedule a checkup for this PDL?</p>
              </div>
              
              <div className={styles.pdlDetailsCard}>
                {/* PDL Photo Section */}
                <div className={styles.pdlPhotoSection}>
                  {selectedPDLForSchedule.photo_of_pdl ? (
                    <img 
                      src={`/api/storage/${selectedPDLForSchedule.photo_of_pdl}`} 
                      alt="PDL"
                      className={styles.pdlPhoto}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={styles.pdlPhotoPlaceholder} style={{display: selectedPDLForSchedule.photo_of_pdl ? 'none' : 'flex'}}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>No Photo</span>
                  </div>
                </div>

                {/* PDL Details */}
                <div className={styles.pdlDetailsGrid}>
                  <div className={styles.pdlDetailItem}>
                    <label>Full Name:</label>
                    <span>
                      {selectedPDLForSchedule.last_name}, {selectedPDLForSchedule.first_name} 
                      {selectedPDLForSchedule.middle_name && ` ${selectedPDLForSchedule.middle_name}`}
                    </span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>AKA / Alias:</label>
                    <span>{selectedPDLForSchedule.alias_nickname || selectedPDLForSchedule.aka || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Sex:</label>
                    <span>{selectedPDLForSchedule.sex === 'M' ? 'Male' : selectedPDLForSchedule.sex === 'F' ? 'Female' : selectedPDLForSchedule.sex || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Age:</label>
                    <span>{calculateAge(selectedPDLForSchedule.date_of_birth)} years old</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Civil Status:</label>
                    <span>{selectedPDLForSchedule.civil_status || 'N/A'}</span>
                  </div>

                  <div className={styles.pdlDetailItem}>
                    <label>Current Cell:</label>
                    <span>
                      {selectedPDLForSchedule.cell?.cell_no || 'Unassigned'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.confirmActions}>
                <button 
                  onClick={cancelScheduleCheckup} 
                  className={styles.cancelConfirmButton}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmScheduleCheckup} 
                  className={styles.confirmSelectButton}
                >
                  Schedule Checkup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Date Picker Modal for Schedule Checkup */}
      {showFollowUpDateModal && (() => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        
        const currentMonth = monthNames[followUpCurrentDate.getMonth()];
        const currentYear = followUpCurrentDate.getFullYear();
        
        const firstDayOfMonth = new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth(), 1);
        const lastDayOfMonth = new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() + 1, 0);
        const firstDayWeekday = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();
        const prevMonthLastDay = new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth(), 0).getDate();
        const prevMonthDays = firstDayWeekday;
        
        const calendarDays = [];
        
        for (let i = prevMonthDays - 1; i >= 0; i--) {
          calendarDays.push({
            day: prevMonthLastDay - i,
            isCurrentMonth: false,
            date: new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() - 1, prevMonthLastDay - i)
          });
        }
        
        for (let i = 1; i <= daysInMonth; i++) {
          calendarDays.push({
            day: i,
            isCurrentMonth: true,
            date: new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth(), i)
          });
        }
        
        const totalDisplayDays = 42;
        const remainingDays = totalDisplayDays - calendarDays.length;
        for (let i = 1; i <= remainingDays; i++) {
          calendarDays.push({
            day: i,
            isCurrentMonth: false,
            date: new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() + 1, i)
          });
        }
        
        const isToday = (date) => {
          const today = new Date();
          return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
        };
        
        const isSelected = (date) => {
          return followUpSelectedDates.some(selectedDate =>
            date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear()
          );
        };
        
        return (
          <div className={styles.followUpDateModalOverlay} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
            <div className={styles.followUpDateModalContent} style={{ background: '#1e293b', borderRadius: '16px', padding: '2rem', maxWidth: '900px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
              <button 
                onClick={cancelScheduleDatePicker}
                style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', cursor: 'pointer' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <div style={{ width: '48px', height: '48px', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', color: '#60a5fa' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.5rem', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>Select Follow-up Date</h3>
                <p style={{ color: '#94a3b8', margin: 0 }}>Choose a date for the follow-up consultation</p>
              </div>
              
              <div style={{ display: 'flex', gap: '2rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ color: '#e2e8f0', margin: 0 }}>{currentMonth} {currentYear}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => setFollowUpCurrentDate(new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() - 1, 1))}
                        style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', cursor: 'pointer' }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <polyline points="15,18 9,12 15,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button 
                        onClick={() => setFollowUpCurrentDate(new Date(followUpCurrentDate.getFullYear(), followUpCurrentDate.getMonth() + 1, 1))}
                        style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', cursor: 'pointer' }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <polyline points="9,18 15,12 9,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                    {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                      <div key={day} style={{ textAlign: 'center', fontSize: '0.75rem', color: '#64748b', fontWeight: 600, padding: '0.5rem 0' }}>
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
                    {calendarDays.map((day, index) => {
                      const isPast = day.date < new Date().setHours(0, 0, 0, 0);
                      const selected = isSelected(day.date);
                      const today = isToday(day.date);
                      
                      return (
                        <button
                          key={index}
                          onClick={() => !isPast && day.isCurrentMonth && toggleDateSelection(day.date)}
                          disabled={isPast || !day.isCurrentMonth}
                          style={{
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: selected ? '2px solid #60a5fa' : today ? '2px solid #f59e0b' : '1px solid rgba(71, 85, 105, 0.3)',
                            background: selected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 41, 59, 0.5)',
                            color: !day.isCurrentMonth ? '#64748b' : isPast ? '#475569' : '#e2e8f0',
                            cursor: isPast || !day.isCurrentMonth ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: selected || today ? 600 : 400,
                            opacity: !day.isCurrentMonth || isPast ? 0.5 : 1
                          }}
                        >
                          {day.day}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div style={{ width: '300px', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '12px', padding: '1.5rem', border: '1px solid rgba(71, 85, 105, 0.3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(71, 85, 105, 0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#60a5fa', fontSize: '0.875rem', fontWeight: 600 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Selected ({followUpSelectedDates.length})
                    </div>
                    {followUpSelectedDates.length > 0 && (
                      <button 
                        onClick={clearAllSelectedDates}
                        style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', padding: '0.25rem 0.5rem', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Clear All
                      </button>
                    )}
                  </div>
                  
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {followUpSelectedDates.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#64748b' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 1rem' }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <p style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>No dates selected</p>
                        <span style={{ fontSize: '0.75rem' }}>Click on dates to select</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {[...followUpSelectedDates].sort((a, b) => a - b).map((date, index) => (
                          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{ width: '24px', height: '24px', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#60a5fa', fontWeight: 600 }}>
                                  {date.toLocaleDateString('en-US', { weekday: 'long' })}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
                                  {date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </div>
                              </div>
                            </div>
                            <button 
                              onClick={() => toggleDateSelection(date)}
                              style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', cursor: 'pointer' }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(71, 85, 105, 0.3)' }}>
                <button 
                  onClick={cancelScheduleDatePicker}
                  style={{ padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #64748b, #475569)', border: 'none', borderRadius: '12px', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={finalizeScheduleCheckup}
                  disabled={followUpSelectedDates.length === 0}
                  style={{ padding: '0.75rem 2rem', background: followUpSelectedDates.length === 0 ? '#475569' : 'linear-gradient(135deg, #06b6d4, #2563eb)', border: 'none', borderRadius: '12px', color: 'white', fontWeight: 600, cursor: followUpSelectedDates.length === 0 ? 'not-allowed' : 'pointer', fontSize: '0.875rem', opacity: followUpSelectedDates.length === 0 ? 0.5 : 1 }}
                >
                  Confirm & Submit ({followUpSelectedDates.length} dates)
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Confirm Deletion</h3>
            </div>
            <div className={styles.modalBody}>
              {deleteSuccess ? (
                <div className={styles.successMessage}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#10b981" strokeWidth="2"/>
                  </svg>
                  <p>Consultation record deleted successfully!</p>
                </div>
              ) : (
                <>
                  <p>Are you sure you want to delete the consultation record for:</p>
                  <div className={styles.recordInfo}>
                    <strong>{recordToDelete?.last_name}, {recordToDelete?.first_name}</strong>
                    <br />
                    Patient ID: {recordToDelete?.patient_id}
                  </div>
                  {deleteError && (
                    <div className={styles.errorMessage}>{deleteError}</div>
                  )}
                  <div className={styles.modalActions}>
                    <button 
                      onClick={() => setShowDeleteModal(false)} 
                      className={styles.cancelButton}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={confirmDelete} 
                      className={styles.confirmDeleteButton}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <div className={styles.spinner}></div>
                          Deleting...
                        </>
                      ) : (
                        'Delete Record'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Schedule Checkup Success Modal */}
      {showScheduleSuccessModal && scheduleSuccessData && (
        <div className={styles.successModalOverlay} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000 }}>
          <div style={{ background: '#1e293b', borderRadius: '16px', padding: '2rem', maxWidth: '500px', width: '90%', border: '1px solid rgba(71, 85, 105, 0.3)' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h3 style={{ fontSize: '1.5rem', color: '#e2e8f0', margin: 0 }}>Checkup Scheduled Successfully</h3>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#cbd5e1', textAlign: 'center', marginBottom: '1rem' }}>
                Checkup has been scheduled for <strong style={{ color: '#60a5fa' }}>{scheduleSuccessData.pdlName}</strong>
              </p>
              <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#60a5fa' }}>Scheduled Dates:</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {scheduleSuccessData.dates.map((date, index) => (
                    <li key={index} style={{ padding: '0.25rem 0', color: '#e2e8f0' }}>
                      📅 {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button 
                onClick={() => {
                  setShowScheduleSuccessModal(false);
                  setScheduleSuccessData(null);
                }}
                style={{ padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '1rem' }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && (
        <div className={styles.modalOverlay} onClick={cancelThemeChange}>
          <div className={styles.confirmModalContent} onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '400px',
            border: '1px solid #3b82f6'
          }}>
            <div className={styles.modalHeader} style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderBottom: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <h3>🎨 Change Theme</h3>
              <button 
                className={styles.closeButton}
                onClick={cancelThemeChange}
              >
                &times;
              </button>
            </div>
            
            <div className={styles.modalBody} style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem'
              }}>
                {pendingTheme ? '🌙' : '☀️'}
              </div>
              <p style={{ 
                fontSize: '1.1rem',
                marginBottom: '0.5rem',
                color: isDarkMode ? '#e2e8f0' : '#1e293b',
                fontWeight: 600
              }}>
                Switch to {pendingTheme ? 'Dark' : 'Light'} Mode?
              </p>
              <p style={{ 
                fontSize: '0.875rem',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                marginBottom: '1.5rem'
              }}>
                Your preference will be saved
              </p>
            </div>
            
            <div className={styles.confirmActions}>
              <button 
                className={styles.cancelConfirmButton}
                onClick={cancelThemeChange}
              >
                Cancel
              </button>
              <button 
                className={styles.confirmSelectButton}
                onClick={confirmThemeChange}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsultSearch;